/**
 * Which photos belong to a chosen colour — one rule for the website product
 * page, its quick-add sheet and the till.
 *
 * Matcha & Sage, 28 Sep 2026: "We changed our dummy images and uploaded our
 * clean images. On the website, when one clicks the image, it shows the dummy
 * image." Frank saw the same after AI clean-ups.
 *
 * A product's MAIN photos (`structuredImages` front/back/side) are its first
 * colour's photos: the product form edits them as one set and saves a copy in
 * `fashionAttributes.colors[0].images`. Every other photo tool — the Image
 * Gallery's Primary tab (replace, AI transform, make primary) and the Photo
 * Studio — changes the main photos alone, so that copy went stale, and the
 * product page showed it the moment a colour was chosen (a one-colour product
 * chooses its colour by itself). The form also re-saves the main photos into
 * the first colour's own gallery slot (`additionalVariantImages[colourId]`,
 * and its first design's look) — a second copy that goes stale the same way
 * (Matcha & Sage's Jumpsuit). So, for a chosen colour:
 *   1. the first colour, in its first design (or none), shows the main
 *      photos — never a copy;
 *   2. a colour + design look shows its own shoot;
 *   3. any other colour shows the photos on its own Image Gallery tab
 *      (`additionalVariantImages[colourId]`, where the tools write), else the
 *      product form's copy (`colors[i].images`).
 * Writers keep `colors[0].images` in step too (syncFirstColourCopy), for the
 * readers that still use it (the app, cart fallbacks).
 *
 * Electronics follow the same rule since Oct 2026 (Bagz Phones: "when a
 * customer selects a particular colour on the website, the images displayed
 * automatically change"): their colours and each colour's own photos live in
 * `electronicsAttributes` (colors, additionalVariantImages — same shape, same
 * key: the colour's id). `colourPhotoAttrs` picks the bucket a product keeps
 * its colours in.
 */

const COPY_ORDER = ["front", "back", "side", "mockup"] as const;
const MAIN_SLOTS = ["front", "back", "side"] as const;

/**
 * Photo-slot URLs in slot order (front, back, side), blanks dropped and one
 * of each: a colour whose photos were saved over with copies of its front
 * (the product form's old edit bug, colourPhotoSlots.ts) shows its photo once,
 * not three times.
 */
function slotUrls(slots: unknown): string[] {
  if (!Array.isArray(slots)) return [];
  const out: string[] = [];
  for (const s of slots as any[]) {
    const u = typeof s === "string" ? s : s?.url || s?.transformedImageUrl || s?.imageUrl || "";
    if (typeof u === "string" && u.length > 0 && !out.includes(u)) out.push(u);
  }
  return out;
}

/**
 * Where a product keeps its colours and each colour's photos: the fashion
 * bucket when it lists colours, else the electronics one (Bagz Phones, Oct
 * 2026). Hand the result to `photosForColour`. Null = no colours at all.
 */
export function colourPhotoAttrs(product: any): any | null {
  const fa = product?.fashionAttributes;
  if (Array.isArray(fa?.colors) && fa.colors.length > 0) return fa;
  const ea = product?.electronicsAttributes;
  if (Array.isArray(ea?.colors) && ea.colors.length > 0) return ea;
  return null;
}

/** A colour's stored copy (`{front, back, side, mockup}`) in that order. */
function copyUrls(images: unknown): string[] {
  if (!images || typeof images !== "object") return [];
  const rec = images as Record<string, unknown>;
  return COPY_ORDER.map((k) => rec[k]).filter(
    (u): u is string => typeof u === "string" && u.length > 0,
  );
}

/**
 * The photos to show for a chosen colour (and design), or null meaning "the
 * product's main photos". Takes `fashionAttributes` — the till has it on a
 * lean card or the full product.
 */
export function photosForColour(
  fashionAttributes: any,
  colourId: string | null | undefined,
  designId?: string | null,
): string[] | null {
  if (!fashionAttributes || !colourId) return null;
  const looks = fashionAttributes.additionalVariantImages ?? {};
  const colours: any[] = Array.isArray(fashionAttributes.colors) ? fashionAttributes.colors : [];
  const designs: any[] = Array.isArray(fashionAttributes.designs) ? fashionAttributes.designs : [];
  const firstColour = colours[0]?.id === colourId;
  if (firstColour && (!designId || designs[0]?.id === designId)) return null;
  if (designId) {
    const look = slotUrls(looks[`${colourId}:${designId}`]);
    if (look.length > 0) return look;
  }
  if (firstColour) return null;
  const own = slotUrls(looks[colourId]);
  if (own.length > 0) return own;
  const copy = copyUrls(colours.find((c) => c?.id === colourId)?.images);
  return copy.length > 0 ? copy : null;
}

/**
 * `fashionAttributes` with the first colour's stored copy brought back in
 * step with the main photos — for a tool that has just changed them — or null
 * when nothing differs (no colours, or already equal). The copy's mockup and
 * every other colour are left as they are.
 */
export function syncFirstColourCopy(fashionAttributes: any, structuredImages: any): any | null {
  const colours: any[] = Array.isArray(fashionAttributes?.colors) ? fashionAttributes.colors : [];
  const first = colours[0];
  if (!first || !structuredImages) return null;
  const images: Record<string, string> = { ...(first.images ?? {}) };
  const storageIds: Record<string, string> = { ...(first.storageIds ?? {}) };
  let changed = false;
  for (const k of MAIN_SLOTS) {
    const slot = structuredImages[k];
    const url = typeof slot?.url === "string" && slot.url.length > 0 ? slot.url : undefined;
    if (!url || images[k] === url) continue;
    images[k] = url;
    if (typeof slot.storageId === "string" && slot.storageId.length > 0) storageIds[k] = slot.storageId;
    else delete storageIds[k];
    changed = true;
  }
  if (!changed) return null;
  const nextFirst: any = { ...first, images };
  if (Object.keys(storageIds).length > 0) nextFirst.storageIds = storageIds;
  else delete nextFirst.storageIds;
  return { ...fashionAttributes, colors: [nextFirst, ...colours.slice(1)] };
}

/**
 * Extra photos (Matcha & Sage, 1 Oct 2026: "I added an extra image making
 * them 2… the website only shows one"). The Image Gallery's "Add image" puts
 * a photo in `images[]`, which the website never read for a product with its
 * main photos, so it showed one. Those photos are now also listed in
 * `extraPhotos`, and the website shows them after the main photos.
 *
 * Only `extraPhotos` counts: `images[]` also holds old copies of replaced
 * back/side photos and earlier AI results that were never meant for the page.
 * A listed photo that has since become a main photo (Make primary) is not
 * repeated.
 */
export function extraPhotosOf(product: any): string[] {
  const listed: unknown = product?.extraPhotos;
  if (!Array.isArray(listed) || listed.length === 0) return [];
  const main = new Set<string>();
  const slots = product?.structuredImages ?? {};
  for (const k of [...MAIN_SLOTS, "mockup"]) {
    const slot = slots?.[k];
    if (typeof slot?.url === "string") main.add(slot.url);
    if (typeof slot?.sourceUrl === "string") main.add(slot.sourceUrl);
  }
  const out: string[] = [];
  for (const u of listed) {
    if (typeof u !== "string" || u.length === 0 || main.has(u) || out.includes(u)) continue;
    out.push(u);
  }
  return out;
}

/**
 * The photo list a photo tool keeps after changing one photo: `from` replaced
 * by `to` (an AI result), or dropped when `to` is null. Order is kept.
 */
export function swapExtraPhoto(list: unknown, from: string, to: string | null): string[] | undefined {
  if (!Array.isArray(list)) return undefined;
  const next: string[] = [];
  for (const u of list) {
    if (u !== from) next.push(u);
    else if (to && !next.includes(to)) next.push(to);
  }
  return next;
}
